import { useEffect, useMemo, useState } from 'react';
import { ListChecks, Loader2, Search, X } from 'lucide-react';
import { parseSkuList } from '@/lib/interfacesCatalog';
import { interfacesService, type ErpItem } from '@/lib/interfacesService';

const MAX_ROWS = 300;

function RivhitItemPicker({ companyId, existing, onAdd, onClose }: {
  companyId: string;
  existing: Set<string>;
  onAdd: (skus: string[]) => void;
  onClose: () => void;
}) {
  const [items, setItems] = useState<ErpItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [picked, setPicked] = useState<Set<string>>(new Set());

  useEffect(() => {
    let alive = true;
    interfacesService.listErpItems(companyId).then(r => {
      if (!alive) return;
      if (r.ok) setItems(r.data); else setError(r.error);
    });
    return () => { alive = false; };
  }, [companyId]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [onClose]);

  const filtered = useMemo(() => {
    if (!items) return [];
    const q = query.trim().toLowerCase();
    return q ? items.filter(i => i.sku.toLowerCase().includes(q) || i.name.toLowerCase().includes(q)) : items;
  }, [items, query]);
  const shown = filtered.slice(0, MAX_ROWS);

  const toggle = (sku: string) => setPicked(prev => {
    const next = new Set(prev);
    if (next.has(sku)) next.delete(sku); else next.add(sku);
    return next;
  });
  const pickAllFiltered = () => setPicked(prev => new Set([...prev, ...filtered.map(i => i.sku).filter(s => !existing.has(s))]));

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" dir="rtl">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative flex max-h-[85vh] w-full max-w-2xl flex-col rounded-2xl bg-white shadow-2xl">
        <div className="flex items-start justify-between gap-4 border-b border-gray-200 px-5 py-4">
          <div>
            <h3 className="text-base font-bold text-gray-900">בחירת מק"טים מרווחית</h3>
            <p className="mt-0.5 text-xs text-gray-500">הרשימה נטענת ישירות מרווחית ולא נשמרת במערכת. נשמרים רק המק"טים שתבחרו.</p>
          </div>
          <button onClick={onClose} className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700" aria-label="סגור">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-2 border-b border-gray-100 px-5 py-3">
          <div className="relative min-w-[200px] flex-1">
            <Search className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              autoFocus
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="חיפוש לפי מק&quot;ט או שם פריט"
              className="w-full rounded-lg border border-gray-300 py-2 pl-3 pr-9 text-sm focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-200"
            />
          </div>
          <button
            type="button"
            onClick={pickAllFiltered}
            disabled={!filtered.length}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50"
          >
            סמן את כל התוצאות ({filtered.length})
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {!items && !error && (
            <div className="flex items-center justify-center gap-2 py-16 text-sm text-gray-500">
              <Loader2 className="h-5 w-5 animate-spin" /> טוען פריטים מרווחית...
            </div>
          )}
          {error && <p className="m-5 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}
          {items && !filtered.length && <p className="py-16 text-center text-sm text-gray-500">לא נמצאו פריטים</p>}
          {!!shown.length && (
            <ul className="divide-y divide-gray-100">
              {shown.map(i => {
                const already = existing.has(i.sku);
                const checked = already || picked.has(i.sku);
                return (
                  <li key={i.sku}>
                    <label className={`flex items-center gap-3 px-5 py-2.5 text-sm transition-colors ${already ? 'cursor-default bg-gray-50' : 'cursor-pointer hover:bg-sky-50/60'}`}>
                      <input type="checkbox" checked={checked} disabled={already} onChange={() => toggle(i.sku)} className="h-4 w-4 rounded border-gray-300 text-sky-600 focus:ring-sky-500" />
                      <span className="w-32 shrink-0 font-mono text-xs text-gray-700" dir="ltr">{i.sku}</span>
                      <span className="flex-1 truncate text-gray-800">{i.name || '—'}</span>
                      <span className="shrink-0 text-xs text-gray-500">{already ? 'כבר ברשימה' : `מלאי ${i.quantity}`}</span>
                    </label>
                  </li>
                );
              })}
            </ul>
          )}
          {filtered.length > MAX_ROWS && (
            <p className="px-5 py-3 text-xs text-gray-500">מוצגים {MAX_ROWS} הראשונים מתוך {filtered.length}. חפשו כדי לצמצם, או השתמשו ב"סמן את כל התוצאות".</p>
          )}
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-gray-200 px-5 py-3">
          <span className="text-sm text-gray-600">נבחרו {picked.size}</span>
          <div className="flex gap-2">
            <button onClick={onClose} className="rounded-lg px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100">ביטול</button>
            <button
              onClick={() => { onAdd([...picked]); onClose(); }}
              disabled={!picked.size}
              className="rounded-lg bg-sky-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-sky-700 disabled:opacity-50"
            >
              הוסף לרשימה
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function SkuListField({ companyId, value, disabled, onChange }: {
  companyId: string;
  value: string;
  disabled: boolean;
  onChange: (v: string) => void;
}) {
  const [picking, setPicking] = useState(false);
  const parsed = useMemo(() => parseSkuList(value), [value]);
  const existing = useMemo(() => new Set(parsed.exact), [parsed]);

  const addSkus = (skus: string[]) => {
    const base = value.trim();
    onChange([base, ...skus].filter(Boolean).join('\n'));
  };

  const counts = [`${parsed.exact.length} מק"טים`];
  if (parsed.prefixes.length) counts.push(`${parsed.prefixes.length} קידומות`);

  return (
    <div>
      <textarea
        dir="ltr"
        rows={6}
        value={value}
        disabled={disabled}
        onChange={e => onChange(e.target.value)}
        onBlur={() => {
          const p = parseSkuList(value);
          const clean = [...p.exact, ...p.prefixes.map(x => `${x}*`)].join('\n');
          if (clean !== value) onChange(clean);
        }}
        placeholder={'A-1001\nA-1002\nW22-*'}
        className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 font-mono text-sm focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-200 disabled:bg-gray-50"
      />
      <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
        <span className="text-xs text-gray-500">{counts.join(' · ')}</span>
        {!disabled && (
          <button
            type="button"
            onClick={() => setPicking(true)}
            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
          >
            <ListChecks className="h-4 w-4" /> בחר מרווחית
          </button>
        )}
      </div>
      {picking && <RivhitItemPicker companyId={companyId} existing={existing} onAdd={addSkus} onClose={() => setPicking(false)} />}
    </div>
  );
}
